import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Upload, FileText, ClipboardPaste, Plus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface ExperienceStepProps {
  data: {
    type: string;
    cvFile: File | null;
    pastedText: string;
    workHistory: Array<{
      jobTitle: string;
      company: string;
      duration: string;
      description: string;
    }>;
    skills: string[];
  };
  onUpdate: (data: any) => void;
  onNext: () => void;
  onBack: () => void;
}

export const ExperienceStep = ({ data, onUpdate, onNext, onBack }: ExperienceStepProps) => {
  const [selectedType, setSelectedType] = useState(data.type || "");
  const [cvFile, setCvFile] = useState<File | null>(data.cvFile);
  const [pastedText, setPastedText] = useState(data.pastedText || "");
  const [workHistory, setWorkHistory] = useState(data.workHistory || []);
  const [skills, setSkills] = useState(data.skills || []);
  const [showWorkForm, setShowWorkForm] = useState(false);
  const { toast } = useToast();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Please upload a file smaller than 5MB.",
          variant: "destructive",
        });
        return;
      }
      setCvFile(file);
      setSelectedType("upload");
      onUpdate({ ...data, type: "upload", cvFile: file });
    }
  };

  const handlePasteChange = (value: string) => {
    setPastedText(value);
    setSelectedType("paste");
    onUpdate({ ...data, type: "paste", pastedText: value });
  };

  const addWorkEntry = () => {
    const newEntry = {
      jobTitle: "",
      company: "",
      duration: "",
      description: "",
    };
    setWorkHistory([...workHistory, newEntry]);
    setShowWorkForm(true);
  };

  const updateWorkEntry = (index: number, field: string, value: string) => {
    const updated = [...workHistory];
    updated[index] = { ...updated[index], [field]: value };
    setWorkHistory(updated);
    setSelectedType("build");
    onUpdate({ ...data, type: "build", workHistory: updated });
  };

  const handleNext = () => {
    if (!selectedType) {
      toast({
        title: "Experience Required",
        description: "Please share your experience using one of the options below.",
        variant: "destructive",
      });
      return;
    }

    if (selectedType === "upload" && !cvFile) {
      toast({
        title: "No File Selected",
        description: "Please upload your CV or choose another option.",
        variant: "destructive",
      });
      return;
    }

    if (selectedType === "paste" && !pastedText.trim()) {
      toast({
        title: "Experience Required",
        description: "Please paste your experience or choose another option.",
        variant: "destructive",
      });
      return;
    }

    if (selectedType === "build" && workHistory.length === 0) {
      toast({
        title: "Experience Required",
        description: "Please add at least one work experience entry.",
        variant: "destructive",
      });
      return;
    }

    onNext();
  };

  return (
    <>
      <CardHeader>
        <CardTitle className="text-2xl">Showcase your experience</CardTitle>
        <p className="text-muted-foreground">
          Choose the easiest way for you to share your work experience.
        </p>
      </CardHeader>

      <CardContent className="space-y-6 pb-8">
        {/* Upload CV Option */}
        <div
          className={`border-2 rounded-lg p-6 transition-all cursor-pointer hover:border-primary/50 ${
            selectedType === "upload" ? "border-primary bg-primary/5" : "border-border"
          }`}
        >
          <label htmlFor="cv-upload" className="cursor-pointer">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-primary/10 rounded-lg">
                <Upload className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg mb-1">Upload CV/Resume</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  PDF or DOCX format, max 5MB
                </p>
                {cvFile && (
                  <p className="text-sm font-medium text-primary">
                    ✓ {cvFile.name}
                  </p>
                )}
                {!cvFile && (
                  <Button variant="outline" size="sm" asChild>
                    <span>Choose File</span>
                  </Button>
                )}
              </div>
            </div>
            <input
              id="cv-upload"
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={handleFileChange}
              className="hidden"
            />
          </label>
        </div>

        {/* Build Profile Option */}
        <div
          className={`border-2 rounded-lg p-6 transition-all ${
            selectedType === "build" ? "border-primary bg-primary/5" : "border-border"
          }`}
        >
          <div className="flex items-start gap-4 mb-4">
            <div className="p-3 bg-accent/10 rounded-lg">
              <FileText className="h-6 w-6 text-accent" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-lg mb-1">Build a Simple Profile</h3>
              <p className="text-sm text-muted-foreground">
                List your work experience and skills directly
              </p>
            </div>
          </div>

          {workHistory.length > 0 && (
            <div className="space-y-4 mb-4">
              {workHistory.map((entry, index) => (
                <div key={index} className="bg-background p-4 rounded-lg space-y-3 border">
                  <div className="grid gap-3">
                    <div>
                      <Label className="text-xs">Job Title</Label>
                      <Input
                        placeholder="e.g., Warehouse Worker"
                        value={entry.jobTitle}
                        onChange={(e) => updateWorkEntry(index, "jobTitle", e.target.value)}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Company</Label>
                      <Input
                        placeholder="e.g., ABC Logistics"
                        value={entry.company}
                        onChange={(e) => updateWorkEntry(index, "company", e.target.value)}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Duration</Label>
                      <Input
                        placeholder="e.g., 2020-2022"
                        value={entry.duration}
                        onChange={(e) => updateWorkEntry(index, "duration", e.target.value)}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">What did you do?</Label>
                      <Textarea
                        placeholder="Briefly describe your responsibilities..."
                        value={entry.description}
                        onChange={(e) => updateWorkEntry(index, "description", e.target.value)}
                        rows={2}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <Button
            variant="outline"
            onClick={addWorkEntry}
            className="w-full"
            size="sm"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Work Experience
          </Button>
        </div>

        {/* Paste Option */}
        <div
          className={`border-2 rounded-lg p-6 transition-all ${
            selectedType === "paste" ? "border-primary bg-primary/5" : "border-border"
          }`}
        >
          <div className="flex items-start gap-4 mb-4">
            <div className="p-3 bg-secondary/10 rounded-lg">
              <ClipboardPaste className="h-6 w-6 text-secondary" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-lg mb-1">Copy & Paste Your Experience</h3>
              <p className="text-sm text-muted-foreground mb-3">
                If you have it saved elsewhere
              </p>
            </div>
          </div>
          <Textarea
            placeholder="Paste your work experience here..."
            value={pastedText}
            onChange={(e) => handlePasteChange(e.target.value)}
            rows={6}
            className="text-base"
          />
        </div>

        <div className="flex gap-3 pt-4">
          <Button variant="outline" onClick={onBack} className="flex-1">
            Back
          </Button>
          <Button onClick={handleNext} className="flex-1">
            Next: Your Skills
          </Button>
        </div>

        <div className="text-center">
          <button className="text-sm text-muted-foreground hover:text-foreground underline">
            Save and finish later
          </button>
        </div>
      </CardContent>
    </>
  );
};
